import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampus } from '../context/CampusContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft, Users, Building, Settings, BarChart3, Plus, Pencil, Trash2,
  Check, X, Loader2, Sparkles, Shield, Database, Leaf, Wine, Trophy, Heart,
  Key, Bot, Save, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ICON_OPTIONS = [
  { id: 'Shield', name: 'Escudo', icon: Shield },
  { id: 'Database', name: 'Base de datos', icon: Database },
  { id: 'Leaf', name: 'Hoja', icon: Leaf },
  { id: 'Wine', name: 'Vino', icon: Wine },
  { id: 'Trophy', name: 'Trofeo', icon: Trophy },
  { id: 'Heart', name: 'Corazón', icon: Heart },
  { id: 'Building', name: 'Edificio', icon: Building },
  { id: 'Bot', name: 'Robot', icon: Bot },
];

const COLOR_OPTIONS = [
  '#06B6D4', '#8B5CF6', '#10B981', '#BE123C', '#F97316', '#14B8A6',
  '#6366F1', '#EC4899', '#EAB308', '#84CC16', '#3B82F6', '#EF4444'
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const { refreshCampuses } = useCampus();
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);

  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [llmOptions, setLlmOptions] = useState({});

  // Modal states
  const [showCampusModal, setShowCampusModal] = useState(false);
  const [showNicheModal, setShowNicheModal] = useState(false);
  const [editingCampus, setEditingCampus] = useState(null);
  const [editingNiche, setEditingNiche] = useState(null);
  const [selectedCampusForNiche, setSelectedCampusForNiche] = useState(null);

  // Form states
  const [campusForm, setCampusForm] = useState({ id: '', name: '', description: '', color: '#6366F1', icon: 'Building' });
  const [nicheForm, setNicheForm] = useState({ name: '', sub_niches: '' });
  const [settingsForm, setSettingsForm] = useState({
    valueserp_api_key: '',
    openai_api_key: '',
    default_llm_provider: 'openai',
    default_llm_model: 'gpt-5.2'
  });

  // Check admin access
  useEffect(() => {
    if (user && user.role !== 'admin') {
      toast.error('No tienes permisos de administrador');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load data
  useEffect(() => {
    if (user?.role === 'admin') {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadUsers(),
        loadCampuses(),
        loadSettings(),
        loadLlmOptions()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadCampuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/campuses`, { headers: getAuthHeaders() });
      setCampuses(response.data);
    } catch (error) {
      console.error('Error loading campuses:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/settings`, { headers: getAuthHeaders() });
      setSettings(response.data);
      setSettingsForm(prev => ({
        ...prev,
        default_llm_provider: response.data.default_llm_provider,
        default_llm_model: response.data.default_llm_model
      }));
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadLlmOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/llm-options`, { headers: getAuthHeaders() });
      setLlmOptions(response.data);
    } catch (error) {
      console.error('Error loading LLM options:', error);
    }
  };

  // User actions
  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API_URL}/admin/users/${userId}`, updates, { headers: getAuthHeaders() });
      toast.success('Usuario actualizado');
      loadUsers();
    } catch (error) {
      toast.error('Error al actualizar usuario');
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('Usuario eliminado');
      loadUsers();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  // Campus actions
  const saveCampus = async () => {
    try {
      if (editingCampus) {
        await axios.put(`${API_URL}/admin/campuses/${editingCampus.id}`, campusForm, { headers: getAuthHeaders() });
        toast.success('Campus actualizado');
      } else {
        if (!campusForm.id || !campusForm.name) {
          toast.error('ID y nombre son obligatorios');
          return;
        }
        await axios.post(`${API_URL}/admin/campuses`, campusForm, { headers: getAuthHeaders() });
        toast.success('Campus creado');
      }
      setShowCampusModal(false);
      setEditingCampus(null);
      setCampusForm({ id: '', name: '', description: '', color: '#6366F1', icon: 'Building' });
      loadCampuses();
      loadStats();
      refreshCampuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar campus');
    }
  };

  const deleteCampus = async (campusId) => {
    try {
      await axios.delete(`${API_URL}/admin/campuses/${campusId}`, { headers: getAuthHeaders() });
      toast.success('Campus eliminado');
      loadCampuses();
      loadStats();
      refreshCampuses();
    } catch (error) {
      toast.error('Error al eliminar campus');
    }
  };

  const openEditCampus = (campus) => {
    setEditingCampus(campus);
    setCampusForm({
      id: campus.id,
      name: campus.name,
      description: campus.description || '',
      color: campus.color,
      icon: campus.icon
    });
    setShowCampusModal(true);
  };

  // Niche actions
  const saveNiche = async () => {
    try {
      const subNiches = nicheForm.sub_niches.split(',').map(s => s.trim()).filter(s => s);

      if (editingNiche) {
        await axios.put(
          `${API_URL}/admin/campuses/${selectedCampusForNiche}/niches/${editingNiche.id}`,
          { name: nicheForm.name, sub_niches: subNiches },
          { headers: getAuthHeaders() }
        );
        toast.success('Nicho actualizado');
      } else {
        await axios.post(
          `${API_URL}/admin/campuses/${selectedCampusForNiche}/niches`,
          { name: nicheForm.name, sub_niches: subNiches },
          { headers: getAuthHeaders() }
        );
        toast.success('Nicho creado');
      }
      setShowNicheModal(false);
      setEditingNiche(null);
      setNicheForm({ name: '', sub_niches: '' });
      loadCampuses();
    } catch (error) {
      toast.error('Error al guardar nicho');
    }
  };

  const deleteNiche = async (campusId, nicheId) => {
    try {
      await axios.delete(`${API_URL}/admin/campuses/${campusId}/niches/${nicheId}`, { headers: getAuthHeaders() });
      toast.success('Nicho eliminado');
      loadCampuses();
    } catch (error) {
      toast.error('Error al eliminar nicho');
    }
  };

  const openAddNiche = (campusId) => {
    setSelectedCampusForNiche(campusId);
    setEditingNiche(null);
    setNicheForm({ name: '', sub_niches: '' });
    setShowNicheModal(true);
  };

  const openEditNiche = (campusId, niche) => {
    setSelectedCampusForNiche(campusId);
    setEditingNiche(niche);
    setNicheForm({
      name: niche.name,
      sub_niches: niche.sub_niches?.join(', ') || ''
    });
    setShowNicheModal(true);
  };

  // Settings actions
  const saveSettings = async () => {
    try {
      await axios.put(`${API_URL}/admin/settings`, settingsForm, { headers: getAuthHeaders() });
      toast.success('Configuración guardada');
      loadSettings();
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = ICON_OPTIONS.find(i => i.id === iconName);
    return iconOption ? iconOption.icon : Building;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-6 py-4 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Panel de Administración</h1>
              <p className="text-xs text-slate-500">Gestiona usuarios, campus y configuración</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white border border-slate-200">
            <TabsTrigger value="stats" className="data-[state=active]:bg-indigo-50" data-testid="tab-stats">
              <BarChart3 className="w-4 h-4 mr-2" />
              Estadísticas
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-indigo-50" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Usuarios
            </TabsTrigger>
            <TabsTrigger value="campuses" className="data-[state=active]:bg-indigo-50" data-testid="tab-campuses">
              <Building className="w-4 h-4 mr-2" />
              Campus y Nichos
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-indigo-50" data-testid="tab-settings">
              <Key className="w-4 h-4 mr-2" />
              API Keys y LLM
            </TabsTrigger>
          </TabsList>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                      <p className="text-sm text-slate-500">Usuarios totales</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                      <Check className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.active_users || 0}</p>
                      <p className="text-sm text-slate-500">Usuarios activos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center">
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_articles || 0}</p>
                      <p className="text-sm text-slate-500">Artículos generados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                      <Building className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_campuses || 0}</p>
                      <p className="text-sm text-slate-500">Campus activos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Articles by Campus */}
            {stats?.articles_by_campus?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Artículos por Campus</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.articles_by_campus.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="font-medium text-slate-700">{item._id || 'Sin campus'}</span>
                        <Badge variant="secondary">{item.count} artículos</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Usuarios</CardTitle>
                <CardDescription>Administra los usuarios del sistema</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4" data-testid="users-list">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-semibold">
                          {u.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                          {u.role === 'admin' ? 'Admin' : 'Usuario'}
                        </Badge>
                        {!u.is_active && <Badge variant="destructive">Inactivo</Badge>}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-slate-500">
                          {u.articles_count} artículos
                        </div>
                        {u.id !== user?.id && (
                          <>
                            <Select
                              value={u.role}
                              onValueChange={(value) => updateUser(u.id, { role: value })}
                            >
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">Usuario</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Switch
                              checked={u.is_active}
                              onCheckedChange={(checked) => updateUser(u.id, { is_active: checked })}
                            />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Se eliminarán también todos sus artículos. Esta acción no se puede deshacer.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteUser(u.id)} className="bg-red-500 hover:bg-red-600">
                                    Eliminar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Campuses Tab */}
          <TabsContent value="campuses">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Campus y Nichos</h2>
                <p className="text-sm text-slate-500">Gestiona los campus y sus nichos de contenido</p>
              </div>
              <Button
                onClick={() => { setEditingCampus(null); setCampusForm({ id: '', name: '', description: '', color: '#6366F1', icon: 'Building' }); setShowCampusModal(true); }}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="add-campus-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Campus
              </Button>
            </div>

            <div className="space-y-6" data-testid="campuses-list">
              {campuses.map((campus) => {
                const IconComponent = getIconComponent(campus.icon);
                return (
                  <Card key={campus.id} className={!campus.is_active ? 'opacity-50' : ''}>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: campus.color + '20', color: campus.color }}
                          >
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{campus.name}</CardTitle>
                            <CardDescription>{campus.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={campus.is_active}
                            onCheckedChange={(checked) =>
                              axios.put(`${API_URL}/admin/campuses/${campus.id}`, { is_active: checked }, { headers: getAuthHeaders() })
                                .then(() => { toast.success('Campus actualizado'); loadCampuses(); })
                            }
                          />
                          <Button variant="ghost" size="sm" onClick={() => openEditCampus(campus)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar campus?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteCampus(campus.id)} className="bg-red-500 hover:bg-red-600">
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-medium text-slate-700">Nichos</Label>
                        <Button variant="outline" size="sm" onClick={() => openAddNiche(campus.id)}>
                          <Plus className="w-3 h-3 mr-1" />
                          Añadir nicho
                        </Button>
                      </div>
                      {campus.niches?.length > 0 ? (
                        <div className="space-y-2">
                          {campus.niches.map((niche) => (
                            <div key={niche.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium text-slate-700">{niche.name}</p>
                                {niche.sub_niches?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {niche.sub_niches.map((sub, idx) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">{sub}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openEditNiche(campus.id, niche)}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500"
                                  onClick={() => deleteNiche(campus.id, niche.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 italic">Sin nichos definidos</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* API Keys */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    API Keys
                  </CardTitle>
                  <CardDescription>Configura las claves de API para los servicios</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>ValueSerp API Key</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="password"
                        placeholder={settings?.valueserp_api_key_set ? '••••••••••••••••' : 'No configurada'}
                        value={settingsForm.valueserp_api_key}
                        onChange={(e) => setSettingsForm(prev => ({ ...prev, valueserp_api_key: e.target.value }))}
                        data-testid="valueserp-key-input"
                      />
                      {settings?.valueserp_api_key_set && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">Activa</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Para búsquedas de Google y preguntas relacionadas</p>
                  </div>
                  <div className="space-y-2">
                    <Label>OpenAI API Key</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="password"
                        placeholder={settings?.openai_api_key_set ? '••••••••••••••••' : 'No configurada'}
                        value={settingsForm.openai_api_key}
                        onChange={(e) => setSettingsForm(prev => ({ ...prev, openai_api_key: e.target.value }))}
                        data-testid="openai-key-input"
                      />
                      {settings?.openai_api_key_set && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700">Activa</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">Para generación de contenido con GPT</p>
                  </div>
                </CardContent>
              </Card>

              {/* LLM Config */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    Modelo de IA
                  </CardTitle>
                  <CardDescription>Selecciona el modelo de IA para generar contenido</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Proveedor</Label>
                    <Select
                      value={settingsForm.default_llm_provider}
                      onValueChange={(value) => {
                        setSettingsForm(prev => ({
                          ...prev,
                          default_llm_provider: value,
                          default_llm_model: llmOptions[value]?.[0]?.id || ''
                        }));
                      }}
                    >
                      <SelectTrigger data-testid="llm-provider-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                        <SelectItem value="gemini">Google Gemini</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Modelo</Label>
                    <Select
                      value={settingsForm.default_llm_model}
                      onValueChange={(value) => setSettingsForm(prev => ({ ...prev, default_llm_model: value }))}
                    >
                      <SelectTrigger data-testid="llm-model-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {llmOptions[settingsForm.default_llm_provider]?.map(model => (
                          <SelectItem key={model.id} value={model.id}>{model.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={saveSettings}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Campus Modal */}
      <Dialog open={showCampusModal} onOpenChange={setShowCampusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCampus ? 'Editar Campus' : 'Nuevo Campus'}</DialogTitle>
            <DialogDescription>
              {editingCampus ? 'Modifica los datos del campus' : 'Crea un nuevo campus para tu escuela'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!editingCampus && (
              <div className="space-y-2">
                <Label>ID único</Label>
                <Input
                  placeholder="ej: fintech"
                  value={campusForm.id}
                  onChange={(e) => setCampusForm(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                  data-testid="campus-id-input"
                />
                <p className="text-xs text-slate-500">Sin espacios, solo letras y guiones</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                placeholder="ej: Campus FinTech"
                value={campusForm.name}
                onChange={(e) => setCampusForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="campus-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                placeholder="ej: Tecnología financiera y banca digital"
                value={campusForm.description}
                onChange={(e) => setCampusForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="campus-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${campusForm.color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                      }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setCampusForm(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Icono</Label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ id, name, icon: Icon }) => (
                  <button
                    key={id}
                    className={`p-2 rounded-lg border transition-colors ${campusForm.icon === id ? 'border-indigo-500 bg-indigo-50 text-indigo-600' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    onClick={() => setCampusForm(prev => ({ ...prev, icon: id }))}
                    title={name}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampusModal(false)}>Cancelar</Button>
            <Button onClick={saveCampus} className="bg-indigo-600 hover:bg-indigo-700">
              {editingCampus ? 'Guardar cambios' : 'Crear Campus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Niche Modal */}
      <Dialog open={showNicheModal} onOpenChange={setShowNicheModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingNiche ? 'Editar Nicho' : 'Nuevo Nicho'}</DialogTitle>
            <DialogDescription>
              {editingNiche ? 'Modifica los datos del nicho' : 'Añade un nuevo nicho al campus'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del nicho</Label>
              <Input
                placeholder="ej: Seguridad en la nube"
                value={nicheForm.name}
                onChange={(e) => setNicheForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="niche-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Subnichos (separados por comas)</Label>
              <Input
                placeholder="ej: AWS Security, Azure Security, GCP Security"
                value={nicheForm.sub_niches}
                onChange={(e) => setNicheForm(prev => ({ ...prev, sub_niches: e.target.value }))}
                data-testid="niche-subniches-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNicheModal(false)}>Cancelar</Button>
            <Button onClick={saveNiche} className="bg-indigo-600 hover:bg-indigo-700">
              {editingNiche ? 'Guardar cambios' : 'Crear Nicho'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
